import {
  BondPriceComponent,
  CouponPaymentComponent,
  FaceAmountComponent,
  MaturityDateComponent,
  TransactionCostAmountComponent,
  TransactionRateUpfrontComponent,
  YieldComponent,
} from "@goodfill/domain-properties-components";
import { DateTime } from "luxon";
import { OrderDirection, TimeInForce } from "@goodfill/domain-properties";
import { FormValidationComponent } from "../../../../../form/FormValidationComponent";
import { PricedBond } from "../../../../bonds/PricedBond";
import { CreateOrderExpirationDateInput } from "../../../../order/orderForm/form/CreateOrderExpirationDateInput";
import { OrderFormState } from "../../../../order/orderForm/state/OrderFormState";
import { PresentedPortfolioPosition } from "../PresentedPortfolioPosition";
import { InputNumber, Link } from "@goodfill/ui-components";
import { UserAuthDataBalanceAccount } from "../../../../backendApi/amplifyGraphQl/CustomGetUserData/UserData";
import { BatchOrderLineStatusIndicator } from "../../../../order/batchOrders/BatchOrderLineStatusIndicator";
import { BatchSellRemoveRow } from "./SelectedToBatchSellContext";
import { BatchDialogTableContainer } from "../../../../order/batchOrders/BatchDialogTableContainer";
import { numberFormat } from "../../../../../number/numberFormat";
import { TIMEZONE_NEY_YORK } from "../../../../../locale/timezone";

export type PresentedPortfolioPositionToSell = { position: PresentedPortfolioPosition; bond: PricedBond };

export const BatchSellDialogTable = ({
  rows,
  tradeDate,
  moneyBalanceAccount,
}: {
  rows: PresentedPortfolioPositionToSell[];
  tradeDate: DateTime;
  moneyBalanceAccount: UserAuthDataBalanceAccount;
}) => {
  const rowToId = (row: PresentedPortfolioPositionToSell) => row.position.id;
  const rowToState = (row: PresentedPortfolioPositionToSell) =>
    OrderFormState.createDefault(
      OrderDirection.Sell,
      row.bond,
      tradeDate,
      row.position.id,
      row.position.availableToSell,
    ).withTimeInForce(TimeInForce.CustomFuture, DateTime.local());

  return (
    <BatchDialogTableContainer
      direction={OrderDirection.Sell}
      rows={rows}
      moneyBalanceAccount={moneyBalanceAccount}
      rowToId={rowToId}
      rowToState={rowToState}
      tableRowRenderer={({
        changeAmount,
        changeStatePrice,
        changeYield,
        disabled,
        setExpirationDate,
        validation,
        state,
      }) => {
        const { row, orderState, lifecycleStatus } = state;

        const minPriceNumber = orderState.c.defaultLoadedPrice.mul(100).toNumber();
        const maxYieldNumber = orderState.c.defaultLoadedYield.mul(100).toNumber();

        const minAmountNumber = orderState.i.pricedBond.minimumPiece.amount.toDecimalPlaces(0).toNumber();
        const maxAmountNumber = row.position.availableToSell.toDecimalPlaces(0).toNumber();

        return {
          data: [
            <BatchOrderLineStatusIndicator status={lifecycleStatus} />,
            <BatchSellRemoveRow rowId={row.position.id} disabled={disabled} />,

            // Bond info
            <div data-orderid={row.position.orderTicketId}>{row.position.ticker}</div>,
            row.position.issuerName,
            <CouponPaymentComponent value={row.position.couponPayment} />,
            <MaturityDateComponent value={row.position.maturityDate} timezone={TIMEZONE_NEY_YORK} />,

            // Inputs
            <div className="flex flex-col">
              <div className="flex flex-row items-center">
                <InputNumber
                  data-testid="customPriceInput"
                  neutral={true}
                  border={validation.customLoadedPrice.length > 0 ? "red" : "neutral"}
                  disabled={disabled}
                  leftIcon="%"
                  width={28}
                  min={minPriceNumber}
                  value={orderState.s.customLoadedPrice.mul(100).toDecimalPlaces(2).toNumber()}
                  setValue={changeStatePrice}
                />
                <FormValidationComponent validation={validation.customLoadedPrice} tooltip />
              </div>
              <div className="opacity-50 pt-1">
                Market:{" "}
                <Link onClick={() => changeStatePrice(minPriceNumber)} disabled={disabled}>
                  <BondPriceComponent value={orderState.c.defaultLoadedPrice} />
                </Link>
              </div>
            </div>,
            <div className="flex flex-col">
              <div className="flex flex-row items-center">
                <InputNumber
                  data-testid="customAmount"
                  neutral={true}
                  border={validation.faceAmount.length > 0 ? "red" : "neutral"}
                  disabled={disabled}
                  leftIcon="$"
                  width={28}
                  formatValue={numberFormat}
                  min={minAmountNumber}
                  max={maxAmountNumber}
                  value={orderState.s.faceAmount.amount.toDecimalPlaces(0).toNumber()}
                  setValue={changeAmount}
                />
                <FormValidationComponent validation={validation.faceAmount} tooltip />
              </div>
              <div className="opacity-50 pt-1">
                <Link onClick={() => changeAmount(minAmountNumber)} disabled={disabled}>
                  <FaceAmountComponent value={orderState.i.pricedBond.minimumPiece.amount} />
                </Link>{" "}
                -{" "}
                <Link onClick={() => changeAmount(maxAmountNumber)} disabled={disabled}>
                  <FaceAmountComponent value={row.position.availableToSell} />
                </Link>
              </div>
            </div>,
            <div className="flex flex-col">
              <div className="flex flex-row items-center">
                <InputNumber
                  data-testid="customYieldInput"
                  width={32}
                  border={validation.customLoadedYield.length > 0 ? "red" : "neutral"}
                  disabled={disabled}
                  value={orderState.s.customLoadedYield.mul(100).toDecimalPlaces(2).toNumber()}
                  setValue={changeYield}
                  max={maxYieldNumber}
                  className="input-border"
                  rightIcon="%"
                />
                <FormValidationComponent validation={validation.customLoadedYield} tooltip />
              </div>
              <div className="opacity-50 pt-1">
                Market:{" "}
                <Link onClick={() => changeYield(maxYieldNumber)} disabled={disabled}>
                  <YieldComponent value={orderState.c.defaultLoadedYield} />
                </Link>
              </div>
            </div>,

            orderState.s.expirationDate !== null ? (
              <CreateOrderExpirationDateInput
                showLabel={false}
                timezone={TIMEZONE_NEY_YORK}
                disabled={disabled}
                timeInForce={orderState.s.timeInForce}
                priceVsYield={orderState.s.priceVsYield}
                validation={validation.expirationDate}
                value={orderState.s.expirationDate}
                onChange={setExpirationDate}
                errorInTooltip
              />
            ) : (
              <></>
            ),

            // Calculated order states
            orderState.s.expirationDate !== null ? (
              <YieldComponent value={orderState.getYieldOnExpirationDate(orderState.s.expirationDate)} />
            ) : (
              <></>
            ),
            <TransactionCostAmountComponent value={orderState.c.transactionCostAmount} />,
            <TransactionRateUpfrontComponent value={orderState.c.transactionCostRateUpfront} />,
          ],
        };
      }}
    />
  );
};
